import { useCallback } from 'react';
import { WidgetEventSuccessType } from 'aerosync-web-sdk';
import { useMutation } from '@apollo/client';
import { useStores } from 'contexts/stores';
import { useObserver } from 'mobx-react-lite';

import { ERNIE_DEFAULT_MESSAGES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import { CompleteAggregatorLinkDocument } from 'src/gql/graphql';
import useDutchiePayEnrollment from 'src/hooks/use-dutchiePay-enrollment';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { AGGREGATORS, EnrollmentSteps } from 'src/payments/constants';
import { BankConnectionContexts, ENROLLMENT_EVENTS, EVENT_ACTIONS, useDutchiePayAnalytics } from '..';
import { formatPaymentsError } from '../use-payments-error/use-payments-error';

type UseCompleteAeroSyncConnectionResponse = {
  onSuccessHandler: (payload: WidgetEventSuccessType) => Promise<void>;
};

export const useCompleteAeroSyncConnection = ({
  brandedDutchiePayName,
}: {
  brandedDutchiePayName: string;
}): UseCompleteAeroSyncConnectionResponse => {
  const { User } = useStores();
  const showErnie = useErnie();
  const { dispensary } = useDispensary();
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();
  const [completeAggregatorLink] = useMutation(CompleteAggregatorLinkDocument);
  const userFullName = useObserver(() => User.fullName);

  const {
    completeAggregatorLinkSucceeded,
    aggregatorLinkExited,
    completeAggregatorLinkStarted,
  } = useDutchiePayEnrollment();

  const dispensaryName = dispensary?.name ?? '';
  const dispensaryId = dispensary?.id ?? '';

  const onSuccessHandler = useCallback(
    async ({ connectionId }: WidgetEventSuccessType) => {
      completeAggregatorLinkStarted();
      try {
        const response = await completeAggregatorLink({
          variables: {
            aggregator: AGGREGATORS.AEROSYNC,
            aggregatorToken: connectionId,
            paymentMethodId: '',
            dispensaryName,
            dispensaryId,
          },
        });

        const pendingPaymentMethod = response.data?.completeAggregatorLink.paymentMethods.filter(
          (method) => method?.status === 'pending'
        );

        if (!pendingPaymentMethod || pendingPaymentMethod.length === 0 || !pendingPaymentMethod[0]) {
          throw new Error(ERNIE_DEFAULT_MESSAGES.PAY_BY_BANK);
        }

        const { bankAccountDetails, id: bankDetailsId } = pendingPaymentMethod[0];
        const { bankName, lastDigits, accountAlias } = bankAccountDetails;

        const bankDetails = {
          lastDigits,
          bankName,
          id: bankDetailsId,
          ownerName: userFullName,
          accountName: accountAlias,
        };

        completeAggregatorLinkSucceeded(bankDetails);

        dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
          step: EnrollmentSteps.connectBank,
          action: EVENT_ACTIONS.SUCCEEDED,
          context: BankConnectionContexts.BANK_CONNECTION_AEROSYNC,
        });
      } catch (err) {
        const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(err, brandedDutchiePayName);
        showErnie(errorMarkdown, ernieType, ernieTimeout);
        aggregatorLinkExited();
      }
    },
    [
      completeAggregatorLinkStarted,
      completeAggregatorLink,
      dispensaryName,
      dispensaryId,
      userFullName,
      completeAggregatorLinkSucceeded,
      dutchiePayEventTracker,
      brandedDutchiePayName,
      showErnie,
      aggregatorLinkExited,
    ]
  );

  return {
    onSuccessHandler,
  };
};
